/**
 * User Manager
 *
 * Centralized manager for all user-related data with advanced caching:
 * - User profile with preferences (tied to authenticated user)
 * - TTL-based cache expiration (10 minutes)
 * - Stale-while-revalidate pattern
 * - Memory + localStorage hybrid caching
 * - ETag support (ready for backend implementation)
 *
 * Cache Strategy:
 * 1. Memory cache (runtime, fastest)
 * 2. localStorage (persist between refresh)
 * 3. Background revalidation when stale
 *
 * IMPORTANT: All data managed here should be cleared when user logs out.
 */

import type { User, UserPreferences } from '@/types/auth'
import { userService } from '@/services/userService'
import { CacheManager } from './CacheManager'

// Cache TTL: 10 minutes (in milliseconds)
export const CACHE_TTL_MS = 10 * 60 * 1000

/**
 * Cached user data - can be authenticated or anonymous
 */
export interface CachedUser {
  id: string
  name: string
  nickname: string
  phoneNumber: string
  email?: string
  avatar?: string
  createdAt?: string
  preferences: UserPreferences
  isAnonymous: boolean // True for unauthenticated users with local preferences
}

class UserManager {
  // Generic cache manager instance
  private cache: CacheManager<CachedUser>

  constructor() {
    this.cache = new CacheManager<CachedUser>({
      storageKey: 'meiuca_user_cache',
      ttl: CACHE_TTL_MS,
      version: 2 // Increment version to invalidate old cache format
    })
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  /**
   * Convert User to CachedUser (authenticated)
   */
  private userToCachedUser(user: User): CachedUser {
    const preferences = user.preferences ?? { modalities: [] }
    return {
      ...user,
      name: user.name ?? '',
      nickname: user.nickname ?? '',
      phoneNumber: user.phoneNumber ?? '',
      preferences,
      isAnonymous: false
    }
  }

  /**
   * Convert CachedUser to User (strip isAnonymous flag)
   */
  private cachedUserToUser(cachedUser: CachedUser): User {
    const { isAnonymous, ...user } = cachedUser
    return user
  }

  /**
   * Create anonymous user with preferences
   */
  createAnonymousUser(preferences: UserPreferences): CachedUser {
    return {
      id: 'anonymous',
      name: 'Usuário Anônimo',
      nickname: 'anônimo',
      phoneNumber: '',
      preferences,
      isAnonymous: true
    }
  }

  // ============================================================================
  // Cache Read Operations
  // ============================================================================

  /**
   * Get cached user data (valid cache only)
   */
  get(): User | null {
    const cached = this.cache.getData()
    return cached ? this.cachedUserToUser(cached) : null
  }

  /**
   * Get cached data even if stale
   */
  getStale(): User | null {
    const cached = this.cache.getStale()?.data
    return cached ? this.cachedUserToUser(cached) : null
  }

  /**
   * Get user profile from cache
   */
  getProfile(): User | null {
    return this.get()
  }

  /**
   * Check if cached user is anonymous
   */
  isAnonymous(): boolean {
    const cached = this.cache.getStale()?.data
    return cached?.isAnonymous ?? false
  }

  /**
   * Get user preferences from cache
   */
  getPreferences(): UserPreferences | null {
    const user = this.get()
    return user?.preferences || null
  }

  /**
   * Get ETag for conditional requests
   */
  getETag(): string | undefined {
    return this.cache.getETag()
  }

  // ============================================================================
  // Cache Write Operations
  // ============================================================================

  /**
   * Save authenticated user data to cache
   */
  set(user: User, etag?: string): void {
    const cachedUser = this.userToCachedUser(user)
    this.cache.set(cachedUser, etag)
  }

  /**
   * Save anonymous user with preferences
   */
  setAnonymous(preferences: UserPreferences): void {
    const anonUser = this.createAnonymousUser(preferences)
    this.cache.set(anonUser)
  }

  /**
   * Save profile to cache
   */
  saveProfile(user: User): void {
    this.set(user, this.cache.getETag())
  }

  /**
   * Save preferences to cache (updates user preferences in-place)
   */
  savePreferences(preferences: UserPreferences): void {
    const cached = this.cache.getStale()?.data
    if (cached) {
      const updated: CachedUser = { ...cached, preferences }
      this.cache.set(updated, this.cache.getETag())
    }
  }

  /**
   * Touch cache (update fetchedAt for 304 Not Modified responses)
   */
  touch(etag?: string): void {
    this.cache.touch(etag)
  }

  // ============================================================================
  // Cache Invalidation
  // ============================================================================

  /**
   * Clear all cached data
   */
  clear(): void {
    this.cache.clear()
  }

  /**
   * Clear profile (alias for clear, used by auth service)
   */
  clearProfile(): void {
    this.clear()
  }

  /**
   * Invalidate cache (mark as stale without deleting)
   */
  invalidate(): void {
    this.cache.invalidate()
  }

  // ============================================================================
  // Validation Helpers
  // ============================================================================

  /**
   * Check if cache exists but is stale
   */
  isStale(): boolean {
    return this.cache.isStale()
  }

  /**
   * Get cache age in seconds
   */
  getCacheAge(): number | null {
    return this.cache.getCacheAge()
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return this.cache.getStats()
  }

  // ============================================================================
  // Fetch & Revalidation (Stale-While-Revalidate)
  // ============================================================================

  /**
   * Fetch fresh data from backend
   * Updates cache with new data
   */
  async fetchFresh(): Promise<User> {
    console.log('[UserManager] Fetching fresh data...')

    // Fetch user profile with preferences from /users/me
    const user = await userService.getProfile()

    // Update cache (TODO: extract ETag from response headers when backend supports it)
    this.set(user)

    return user
  }

  /**
   * Revalidate: stale-while-revalidate pattern
   * Returns cached data immediately, fetches fresh data in background
   */
  async revalidate(): Promise<{
    user: User | null
    isStale: boolean
  }> {
    // Get cached data (even if stale)
    const user = this.getStale()
    const cached = this.cache.getStale()
    const isStale = cached ? !this.cache.isValid(cached) : true

    console.log('[UserManager] Revalidate:', {
      hasCache: !!user,
      isStale,
      cacheAge: this.getCacheAge()
    })

    // Return cached data immediately if exists
    const result = {
      user,
      isStale
    }

    // Fetch fresh data in background if stale or missing
    if (isStale) {
      this.fetchFresh().catch(error => {
        console.error('[UserManager] Background revalidation failed:', error)
      })
    }

    return result
  }

  /**
   * Ensure fresh data (blocking)
   * Waits for fresh data if cache is stale/missing
   */
  async ensureFresh(): Promise<User> {
    const user = this.get()

    // Return valid cache
    if (user) {
      console.log('[UserManager] Using valid cache')
      return user
    }

    // Fetch fresh data
    console.log('[UserManager] Cache miss or expired, fetching...')
    return await this.fetchFresh()
  }
}

// Export singleton instance
export const userManager = new UserManager()
